import type { DashboardComponent } from 'common/types/dashboard/dashboardDefinition';

// Exhaustive by construction: adding a component type to the backend registry regenerates
// DashboardComponent and this record stops compiling until the new type gets a label.
export const COMPONENT_TYPE_LABELS: Record<DashboardComponent['type'], string> = {
  bar_chart: 'Bar chart',
  content_card: 'Content',
  line_chart: 'Line chart',
  list: 'Ranked list',
  pie_chart: 'Pie chart',
  table: 'Table',
  totals: 'Totals',
};

const humanizeMetric = (metric: string): string =>
  metric
    .split('_')
    .map(word => (word === 'moic' || word === 'irr' ? word.toUpperCase() : word))
    .join(' ');

// Turns each column name into a short human label so we can show the metric/dimension selections
// on the card, which is the only way to tell whether the AI picked the right fields while there is
// still no data endpoint behind the tiles.
export const describeComponentFields = (component: DashboardComponent): string[] => {
  switch (component.type) {
    case 'totals':
      return component.fields.metrics.map(humanizeMetric);
    case 'bar_chart':
    case 'line_chart':
      return [
        humanizeMetric(component.fields.dimension),
        ...component.fields.metrics.map(humanizeMetric),
        ...(component.fields.series ?? []).map(humanizeMetric),
      ];
    case 'pie_chart':
      return [humanizeMetric(component.fields.dimension), humanizeMetric(component.fields.metric)];
    case 'list':
      return [
        humanizeMetric(component.fields.metric),
        `top ${component.fields.limit ?? 5} by ${component.fields.entity_kind}`,
      ];
    case 'table': {
      // Either branch of the table union carries an index-keyed column map; the embedded
      // spreadsheet is the shape the backend normalizes everything to.
      const columns =
        'spreadsheet_definition' in component.fields
          ? component.fields.spreadsheet_definition.columns
          : component.fields.columns;
      // Read as unknown despite the generated `string` type. The JSON Schema documents the embedded
      // spreadsheet as a structural hint with the Python validator authoritative, and rows written
      // before that validator rejected non-string column values still hold objects like
      // {"0": {"metric": "moic"}} — which crashed this card. The other cases are safe to trust:
      // metric/metrics/dimension/series are all vocabulary-checked strings on save.
      return Object.values<unknown>(columns ?? {})
        .filter((value): value is string => typeof value === 'string')
        .map(humanizeMetric);
    }
    case 'content_card':
      return component.fields?.metric ? [humanizeMetric(component.fields.metric)] : [];
    default:
      return [];
  }
};
